import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import type { EventInviteStatus } from 'src/v3/core/types'
import { isSessionPast } from './access-window'

dayjs.extend(utc)

export type RsvpState = EventInviteStatus | 'open' | 'past'

export type RegistrationInfo =
  | { state: 'closed' }
  | {
      state: 'open'
      remaining?: number
      licenseRemaining?: number
      licenseType?: string
    }
  | { state: 'waitlisted' }

type RegistrationCapacity =
  | { basis: 'generic'; remaining: number }
  | { basis: 'license'; remaining: number; licenseType: string }

export type RegistrationDisplayInfo =
  | { tag: 'closed' }
  | {
      tag: 'open'
      capacity: RegistrationCapacity | null
      deadline: Dayjs | null
    }
  | { tag: 'waitlisted'; deadline: Dayjs | null }

// Returns the user's actual invite status, or null if they have no invite.
export let deriveRsvpState = (
  invites: { status: EventInviteStatus }[],
): EventInviteStatus | null => invites[0]?.status ?? null

// Returns whether registration is available ('open') or unavailable ('past')
// based solely on time and deadline — independent of invite status.
export let deriveRegistrationState = (
  sessionTime: string,
  endTime: string | undefined,
  registrationDeadline?: string,
): 'open' | 'past' => {
  if (isSessionPast(sessionTime, endTime)) return 'past'
  if (
    registrationDeadline != null &&
    dayjs.utc().isAfter(dayjs.utc(registrationDeadline))
  )
    return 'past'
  return 'open'
}

export let deriveRegistrationDisplayInfo = (
  registrationInfo: RegistrationInfo,
  deadline: Dayjs | null,
): RegistrationDisplayInfo => {
  if (registrationInfo.state === 'closed') return { tag: 'closed' }
  if (registrationInfo.state === 'waitlisted')
    return { tag: 'waitlisted', deadline }

  let capacity: RegistrationCapacity | null =
    registrationInfo.licenseRemaining != null &&
    registrationInfo.licenseType != null
      ? {
          basis: 'license',
          remaining: registrationInfo.licenseRemaining,
          licenseType: registrationInfo.licenseType,
        }
      : registrationInfo.remaining != null
        ? { basis: 'generic', remaining: registrationInfo.remaining }
        : null

  return { tag: 'open', capacity, deadline }
}
